import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../api";
import "./Rubezi.css";

const RUBEZI_STATE_KEY = "rubeziPageState";

const iso = (d) => (d ? String(d).slice(0, 10) : "");

// Defaults for page state
const DEFAULT_RUBEZI = Array.from({ length: 5 }, () => ({
    date: "",
    time: "",
    seized: "",
    executor: "",
}));

const DEFAULT_SEIZED_MODES = Array.from({ length: 5 }, () => "none");

// -------------------- Date picker (sl-SI) --------------------
function DateField({ value, onChange, placeholder = "", disabled }) {
    const [open, setOpen] = useState(false);
    const [view, setView] = useState(() => {
        const d = value ? new Date(value) : new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });

    const boxRef = useRef(null);
    const inputRef = useRef(null);
    const justClosedRef = useRef(false);

    function closeCalendar() {
        setOpen(false);
        justClosedRef.current = true;
        setTimeout(() => {
            justClosedRef.current = false;
        }, 250);
    }

    useEffect(() => {
        function onDoc(e) {
            if (!boxRef.current) return;
            if (e.type === "keydown" && e.key === "Escape") return closeCalendar();
            if (e.type === "mousedown" && !boxRef.current.contains(e.target)) {
                closeCalendar();
            }
        }
        if (open) {
            document.addEventListener("mousedown", onDoc);
            document.addEventListener("keydown", onDoc);
            return () => {
                document.removeEventListener("mousedown", onDoc);
                document.removeEventListener("keydown", onDoc);
            };
        }
    }, [open]);

    const fmtTitle = new Intl.DateTimeFormat("sl-SI", {
        month: "long",
        year: "numeric",
    });
    const fmtCell = new Intl.DateTimeFormat("sl-SI", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
    const fmtHuman = (v) =>
        v ? new Intl.DateTimeFormat("sl-SI").format(new Date(v)) : "";

    const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
    const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const addMonths = (d, n) =>
        new Date(d.getFullYear(), d.getMonth() + n, 1);
    const toISO = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
            2,
            "0"
        )}-${String(d.getDate()).padStart(2, "0")}`;

    const grid = useMemo(() => {
        const first = startOfMonth(view);
        const last = endOfMonth(view);
        const firstWeekday = (first.getDay() + 6) % 7; // Mon=0
        const daysInMonth = last.getDate();
        const cells = [];

        const prev = new Date(view.getFullYear(), view.getMonth(), 0);
        for (let i = firstWeekday - 1; i >= 0; i--) {
            const d = new Date(
                prev.getFullYear(),
                prev.getMonth(),
                prev.getDate() - i
            );
            cells.push({ d, inMonth: false });
        }
        for (let i = 1; i <= daysInMonth; i++) {
            const d = new Date(view.getFullYear(), view.getMonth(), i);
            cells.push({ d, inMonth: true });
        }
        while (cells.length < 42) {
            const lastCell = cells[cells.length - 1].d;
            const d = new Date(
                lastCell.getFullYear(),
                lastCell.getMonth(),
                lastCell.getDate() + 1
            );
            cells.push({ d, inMonth: false });
        }
        return cells;
    }, [view]);

    const selectedISO = value || "";
    const selectedKey = selectedISO ? iso(selectedISO) : "";
    const todayISO = iso(new Date());

    return (
        <div className="rbz-dateField" ref={boxRef}>
            <div className="rbz-dateField__inputWrap">
                <input
                    ref={inputRef}
                    className="rbz-dateField__input rbz-input"
                    type="text"
                    inputMode="none"
                    placeholder={placeholder}
                    value={fmtHuman(selectedISO)}
                    onFocus={() => {
                        if (!justClosedRef.current && !disabled) setOpen(true);
                    }}
                    onClick={() => {
                        if (!justClosedRef.current && !disabled) setOpen(true);
                    }}
                    readOnly
                    disabled={disabled}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                />
                <button
                    className="rbz-dateField__btn"
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setOpen((s) => !s)}
                    aria-label="Odpri koledar"
                    disabled={disabled}
                >
                    ▾
                </button>
            </div>

            {open && (
                <div
                    className="rbz-cal"
                    role="dialog"
                    aria-label="Koledar (sl)"
                >
                    <div className="rbz-cal__head">
                        <button
                            type="button"
                            className="rbz-cal__nav"
                            onClick={() => setView((v) => addMonths(v, -1))}
                            aria-label="Prejšnji mesec"
                        >
                            ‹
                        </button>
                        <div className="rbz-cal__title">
                            {fmtTitle.format(view)}
                        </div>
                        <button
                            type="button"
                            className="rbz-cal__nav"
                            onClick={() => setView((v) => addMonths(v, 1))}
                            aria-label="Naslednji mesec"
                        >
                            ›
                        </button>
                    </div>

                    <div className="rbz-cal__grid">
                        {["P", "T", "S", "Č", "P", "S", "N"].map((d, i) => (
                            <div
                                key={`dow-${i}`}
                                className="rbz-cal__dow"
                                aria-hidden="true"
                            >
                                {d}
                            </div>
                        ))}
                        {grid.map(({ d, inMonth }, i) => {
                            const isoKey = toISO(d);
                            const isSel = selectedKey && isoKey === selectedKey;
                            const isToday = isoKey === todayISO;
                            return (
                                <button
                                    key={`cell-${isoKey}-${i}`}
                                    type="button"
                                    className={
                                        "rbz-cal__cell" +
                                        (inMonth
                                            ? ""
                                            : " rbz-cal__cell--muted") +
                                        (isSel
                                            ? " rbz-cal__cell--sel"
                                            : "") +
                                        (isToday
                                            ? " rbz-cal__cell--today"
                                            : "")
                                    }
                                    onClick={() => {
                                        onChange?.(isoKey);
                                        closeCalendar();
                                        requestAnimationFrame(() =>
                                            inputRef.current?.blur()
                                        );
                                    }}
                                    title={fmtCell.format(d)}
                                >
                                    {d.getDate()}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// -------------------- Time picker (24h, hour + minute columns) --------------------
const HOURS = Array.from({ length: 24 }, (_, h) =>
    String(h).padStart(2, "0")
);
const MINUTES = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

function TimeField({ value, onChange, placeholder = "", disabled }) {
    const [open, setOpen] = useState(false);
    const [tempHour, setTempHour] = useState("00");
    const [tempMinute, setTempMinute] = useState("00");

    const boxRef = useRef(null);
    const inputRef = useRef(null);
    const justClosedRef = useRef(false);

    function syncFromValue() {
        const [h, m] = (value || "").split(":");
        const hh = /^[0-2]\d$/.test(h || "") ? h : "00";
        const mm = /^[0-5]\d$/.test(m || "") ? m : "00";
        setTempHour(hh);
        setTempMinute(mm);
    }

    function closePanel() {
        setOpen(false);
        justClosedRef.current = true;
        setTimeout(() => {
            justClosedRef.current = false;
        }, 250);
    }

    useEffect(() => {
        function onDoc(e) {
            if (!boxRef.current) return;
            if (e.type === "keydown" && e.key === "Escape") return closePanel();
            if (e.type === "mousedown" && !boxRef.current.contains(e.target)) {
                closePanel();
            }
        }
        if (open) {
            document.addEventListener("mousedown", onDoc);
            document.addEventListener("keydown", onDoc);
            return () => {
                document.removeEventListener("mousedown", onDoc);
                document.removeEventListener("keydown", onDoc);
            };
        }
    }, [open]);

    const openPanel = () => {
        if (justClosedRef.current || disabled) return;
        syncFromValue();
        setOpen(true);
    };

    return (
        <div className="rbz-timeField" ref={boxRef}>
            <div className="rbz-timeField__inputWrap">
                <input
                    ref={inputRef}
                    className="rbz-timeField__input rbz-input"
                    type="text"
                    inputMode="numeric"
                    placeholder={placeholder}
                    value={value || ""}
                    onFocus={openPanel}
                    onClick={openPanel}
                    onChange={(e) => onChange?.(e.target.value)}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    disabled={disabled}
                />
                <button
                    className="rbz-timeField__btn"
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={openPanel}
                    aria-label="Odpri izbor ure"
                    disabled={disabled}
                >
                    ▾
                </button>
            </div>

            {open && (
                <div
                    className="rbz-timePanel"
                    role="listbox"
                    aria-label="Izberi uro"
                >
                    <div className="rbz-timePanel__cols">
                        {/* HOURS */}
                        <div className="rbz-timePanel__col">
                            <div className="rbz-timePanel__colLabel">
                                Ura
                            </div>
                            {HOURS.map((h) => {
                                const isSel = h === tempHour;
                                return (
                                    <button
                                        key={h}
                                        type="button"
                                        className={
                                            "rbz-timePanel__item" +
                                            (isSel
                                                ? " rbz-timePanel__item--sel"
                                                : "")
                                        }
                                        onClick={() => {
                                            setTempHour(h);
                                        }}
                                    >
                                        {h}
                                    </button>
                                );
                            })}
                        </div>

                        {/* MINUTES */}
                        <div className="rbz-timePanel__col">
                            <div className="rbz-timePanel__colLabel">
                                Minute
                            </div>
                            {MINUTES.map((m) => (
                                <button
                                    key={m}
                                    type="button"
                                    className="rbz-timePanel__item"
                                    onClick={() => {
                                        const final = `${tempHour}:${m}`;
                                        onChange?.(final);
                                        closePanel();
                                        requestAnimationFrame(() =>
                                            inputRef.current?.blur()
                                        );
                                    }}
                                >
                                    {m}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// -------------------- Main Rubezi page --------------------
export default function Rubezi() {
    const navigate = useNavigate();

    const [executorName, setExecutorName] = useState("");

    const [rubezi, setRubezi] = useState(DEFAULT_RUBEZI);
    const [seizedModes, setSeizedModes] = useState(DEFAULT_SEIZED_MODES);

    const [executorFileNo, setExecutorFileNo] = useState("");
    const [courtCaseNo, setCourtCaseNo] = useState("");

    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState("");

    useEffect(() => {
        if (typeof window === "undefined") return;

        try {
            const raw = window.localStorage.getItem(RUBEZI_STATE_KEY);
            if (!raw) return;

            const data = JSON.parse(raw);
            if (!data || typeof data !== "object") return;

            if (Array.isArray(data.rubezi)) {
                setRubezi(() => {
                    const base = DEFAULT_RUBEZI;
                    return base.map((item, index) => ({
                        ...item,
                        ...(data.rubezi[index] || {}),
                    }));
                });
            }

            if (Array.isArray(data.seizedModes)) {
                setSeizedModes(() => {
                    const base = DEFAULT_SEIZED_MODES;
                    return base.map((_, index) => data.seizedModes[index] || "none");
                });
            }

            if (typeof data.executorFileNo === "string") {
                setExecutorFileNo(data.executorFileNo);
            }

            if (typeof data.courtCaseNo === "string") {
                setCourtCaseNo(data.courtCaseNo);
            }
        } catch {
            // ignore parse/storage errors
        }
    }, []);

    // Fetch logged-in user (executor) and prefill executor field
    useEffect(() => {
        let alive = true;

        (async () => {
            try {
                const res = await apiFetch("/api/auth/me");
                if (!res.ok) return;

                const data = await res.json();
                if (!alive) return;

                const fullName =
                    (data.fullName && data.fullName.trim()) ||
                    [data.firstName, data.lastName].filter(Boolean).join(" ").trim() ||
                    data.email;

                setExecutorName(fullName);

                setRubezi((prev) =>
                    prev.map((r) =>
                        r.executor ? r : { ...r, executor: fullName }
                    )
                );
            } catch {
                // silently ignore; page still works, just no prefill
            }
        })();

        return () => {
            alive = false;
        };
    }, []);

    // Persist state to ONE localStorage key whenever it changes
    useEffect(() => {
        if (typeof window === "undefined") return;

        const hasAnyData =
            (executorFileNo && executorFileNo.trim() !== "") ||
            (courtCaseNo && courtCaseNo.trim() !== "") ||
            rubezi.some(
                (r) =>
                    r.date ||
                    r.time ||
                    (r.seized && r.seized.trim() !== "") ||
                    (r.executor && r.executor.trim() !== "")
            ) ||
            seizedModes.some((m) => m !== "none");

        try {
            if (!hasAnyData) {
                window.localStorage.removeItem(RUBEZI_STATE_KEY);
            } else {
                const stateToSave = {
                    rubezi,
                    seizedModes,
                    executorFileNo,
                    courtCaseNo,
                };
                window.localStorage.setItem(
                    RUBEZI_STATE_KEY,
                    JSON.stringify(stateToSave)
                );
            }
        } catch {
            // ignore storage errors
        }
    }, [rubezi, seizedModes, executorFileNo, courtCaseNo]);

    const handleChange = (index, field, value) => {
        setRubezi((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveMsg("");

        try {
            const payload = {
                executorFileNo,
                courtCaseNo,
                pageData: { rubezi },
            };

            const res = await apiFetch("/api/page/rubezi", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(text || "Napaka pri shranjevanju rubežev.");
            }

            setSaveMsg("Uspešno shranjeno.");
        } catch (e) {
            setSaveMsg(e?.message || "Napaka pri shranjevanju.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <main className="rbz-page">
                <section className="rbz-content">
                    <div className="rbz-card">
                        {/* Header inside card (same pattern as PodatkiUpnika, renamed rubezi-*) */}
                        <div className="rubezi-header">
                            <div className="rubezi-header-brand">
                                <div className="rubezi-header-meta">
                                    <h1 className="rubezi-header-title">Rubeži</h1>
                                    <p className="rubezi-header-subtitle">
                                        Evidenca rubežev z datumom, uro, opisom zarubljenih predmetov in izvršiteljem.
                                    </p>
                                </div>
                            </div>

                            <button
                                type="button"
                                className="btn-nazaj"
                                onClick={() => navigate("/profil", { replace: true })}
                                aria-label="Nazaj na profil"
                                title="Nazaj"
                            >
                                <span className="btn-nazaj__icon" aria-hidden="true">
                                    ←
                                </span>
                            </button>
                        </div>

                        <section className="rbz-section">
                            <div className="rbz-sectionHeader">
                                <h2 className="rbz-sectionTitle">Pregled rubežev</h2>
                            </div>

                            <section className="rbz-grid">
                                {rubezi.map((r, idx) => (
                                    <div key={idx} className="rbz-rubezCard">
                                        <div className="rbz-sectionHeader rbz-sectionHeader--tight">
                                            <h3 className="rbz-sectionTitle">
                                                Rubež {idx + 1}
                                            </h3>
                                        </div>

                                        {/* DATUM – custom Slovenian calendar */}
                                        <label className="rbz-field rbz-field--date">
                                            <span className="rbz-label">
                                                Datum rubeža
                                            </span>
                                            <DateField
                                                value={r.date}
                                                onChange={(v) =>
                                                    handleChange(idx, "date", v)
                                                }
                                            />
                                        </label>

                                        {/* URA – custom 24h time picker (hours + minutes) */}
                                        <label className="rbz-field rbz-field--time">
                                            <span className="rbz-label">
                                                ⏱ Ura rubeža
                                            </span>
                                            <TimeField
                                                value={r.time}
                                                onChange={(v) =>
                                                    handleChange(idx, "time", v)
                                                }
                                            />
                                        </label>

                                        {/* ZARUBLJENO */}
                                        <label className="rbz-field rbz-field--textarea">
                                            <span className="rbz-label">
                                                Opis zarubljenega
                                            </span>

                                            {(() => {
                                                const mode = seizedModes[idx] || "none";

                                                return (
                                                    <>
                                                        <select
                                                            className="rbz-input"
                                                            value={mode}
                                                            onChange={(e) => {
                                                                const newMode = e.target.value;

                                                                setSeizedModes((prev) => {
                                                                    const next = [...prev];
                                                                    next[idx] = newMode;
                                                                    return next;
                                                                });

                                                                if (newMode === "none") {
                                                                    handleChange(idx, "seized", "");
                                                                }
                                                            }}
                                                        >
                                                            <option value="none">- Zarubljeno</option>
                                                            <option value="manual">- Možnost vnašanja</option>
                                                        </select>

                                                        {mode === "manual" && (
                                                            <textarea
                                                                className="rbz-input rbz-input--textarea"
                                                                rows={4}
                                                                value={r.seized}
                                                                onChange={(e) =>
                                                                    handleChange(
                                                                        idx,
                                                                        "seized",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                placeholder="Opis zarubljenih stvari, premičnin, računa ..."
                                                            />
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </label>

                                        {/* IZVRŠITELJ */}
                                        <label className="rbz-field rbz-field--executor">
                                            <span className="rbz-label">Izvršitelj</span>
                                            <input
                                                className="rbz-input rbz-input--readonly"
                                                value={executorName}
                                                readOnly
                                                placeholder="— ni podatkov o izvršitelju —"
                                            />
                                        </label>
                                    </div>
                                ))}
                            </section>
                        </section>

                        <div className="rbz-footer">
                            {saveMsg && (
                                <span className="rbz-saveMsg">{saveMsg}</span>
                            )}

                            <button
                                type="button"
                                className="rbz-btn"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? "Shranjujem…" : "Shrani"}
                            </button>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
}